import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Row, Col, Card, Spin, Typography, Select } from "antd";
import Accordion from "./Accordion";
import DynamicTable from "./DynamicTable";
import DynamicChart from "./DynamicChart";
import { config } from "../../../../config";
import { LoadingOutlined } from "@ant-design/icons";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];

export default function DashboardViewer({ organizationId, dashboardId }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const loadDashboard = useCallback(async () => {
    let currentOrgId = organizationId;

    try {
      setLoading(true);
      setError(null);

      if (!currentOrgId) {
        const namaDomain = window.location.href;
        const orgRes = await axios.get(`${config.BASE_URL}organization/findContentOrganization?namaDomain=${namaDomain}`);
        const dataOrg = orgRes.data?.data;
        if (dataOrg?.organization?.organizationId) {
          currentOrgId = dataOrg.organization.organizationId;
        } else {
          throw new Error("Organisasi tidak ditemukan");
        }
      }

      let token = null;
      try {
        const userCredent = JSON.parse(localStorage.getItem("user_credent"));
        token = userCredent?.accesToken || userCredent?.accessToken;
      } catch (e) { }

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(`${config.BASE_URL_FORM_DINAMIS}api/dashboards/${currentOrgId}/view`, {
        params: { month, year, dashboardId },
        headers
      });

      const dashData = res.data?.data || res.data;
      if (!dashData || !dashData.panels) {
        throw new Error("Dashboard tidak ditemukan");
      }

      const normalizedPanels = dashData.panels.map(p => ({
        ...p,
        id: p.id || p._id,
        components: p.components.map(c => {
          const tableConfig = typeof c.tableConfig === 'string' ? JSON.parse(c.tableConfig) : (c.tableConfig || {});
          const orderBy = typeof c.orderBy === 'string' ? JSON.parse(c.orderBy) : (c.orderBy || []);
          return {
            ...c,
            tableConfig,
            orderBy,
            width: Number(c.width || tableConfig.width || 4),
            height: Number(c.height || tableConfig.height || 300),
            useDualAxis: c.useDualAxis === true || c.useDualAxis === 'true' || tableConfig.useDualAxis === true,
            originalForm: (c.formId && typeof c.formId === 'object') ? c.formId : c.originalForm,
            formId: (c.formId && typeof c.formId === 'object') ? c.formId._id : c.formId,
          };
        })
      }));

      setDashboard({ ...dashData, panels: normalizedPanels });
    } catch (err) {
      console.error(err);
      setError("Data statistik belum tersedia.");
    } finally {
      setLoading(false);
    }
  }, [organizationId, dashboardId, month, year]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 16, minHeight: 400 }}>
      <Spin indicator={<LoadingOutlined spin style={{ color: '#FF5722' }} />} size="large" />
      <Typography.Text type="secondary" style={{ fontSize: 16 }}>
        Memuat Dashboard
      </Typography.Text>
    </div>
  );

  if (error) return <div style={{ padding: 40, textAlign: 'center' }}><Typography.Text type="secondary">{error}</Typography.Text></div>;
  if (!dashboard || dashboard.panels?.length === 0) return <div style={{ padding: 40, textAlign: 'center' }}><Typography.Text type="secondary">Data statistik belum tersedia.</Typography.Text></div>;

  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let y = currentYear - 5; y <= currentYear + 3; y++) {
    availableYears.push(y);
  }

  const availableMonths = MONTHS.map((m, i) => ({ val: i + 1, label: m }));

  return (
    <div style={{ padding: "10px", width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", paddingBottom: "20px", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Select value={year} onChange={(val) => setYear(Number(val))} style={{ width: 100 }}>
            {availableYears.map(y => <Select.Option key={y} value={y}>{y}</Select.Option>)}
          </Select>
          <Select value={month} onChange={(val) => setMonth(Number(val))} style={{ width: 130 }}>
            {availableMonths.map(m => <Select.Option key={m.val} value={m.val}>{m.label}</Select.Option>)}
          </Select>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%" }}>
        {dashboard.panels.sort((a, b) => a.order - b.order).map((panel) => (
          <Accordion key={panel.id || panel._id} title={panel.title} defaultOpen={true}>
            <Row gutter={[16, 16]}>
              {panel.components.sort((a, b) => a.order - b.order).map((comp) => {
                const activeData = comp.chartData || [];
                const totalRow = Array.isArray(activeData) ? activeData.find(r => r._isTotalRow) : null;
                let regularData = Array.isArray(activeData) ? activeData.filter(r => !r._isTotalRow) : [];

                if (regularData.length > 0 && comp.orderBy?.length > 0) {
                  regularData.sort((a, b) => {
                    for (const crit of comp.orderBy) {
                      let valA = a[crit.field];
                      let valB = b[crit.field];
                      if (valA === valB) continue;
                      const factor = crit.direction === "desc" ? -1 : 1;
                      if (typeof valA === "number" && typeof valB === "number") return (valA - valB) * factor;
                      return String(valA ?? "").localeCompare(String(valB ?? ""), undefined, { numeric: true, sensitivity: 'base' }) * factor;
                    }
                    return 0;
                  });
                }

                const finalData = totalRow ? [...regularData, totalRow] : regularData;
                const gridSpan = (24 / 4) * (comp.width || 4);
                const minHeight = comp.height || 300;

                return (
                  <Col key={comp.id} span={gridSpan}>
                    <Card
                      bordered={true}
                      bodyStyle={{
                        padding: "10px",
                        height: minHeight,
                        display: "flex",
                        flexDirection: "column",
                        overflow: comp.type === "table" ? "hidden" : "visible"
                      }}
                      style={{
                        borderRadius: 10,
                        overflow: comp.type === "table" ? "visible" : "hidden",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.03)"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.4 }}>
                          {comp.title}
                        </h3>
                      </div>

                      {finalData ? (
                        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                          {comp.type === "table" ? (
                            <div style={{ overflowX: "auto", flex: 1 }}>
                              <DynamicTable
                                fields={comp.originalForm?.formSchema?.fields || []}
                                data={finalData}
                                pageSize={comp.pageSize}
                                tableConfig={comp.tableConfig}
                                height={minHeight - 65}
                              />
                            </div>
                          ) : (
                            <div style={{ flex: 1 }}>
                              <DynamicChart
                                type={comp.type}
                                data={finalData}
                                height={minHeight}
                                yAxisTitle={comp.tableConfig?.yAxisTitle || "Nilai"}
                                {...comp}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ padding: "0 10px 10px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                          <Spin />
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Accordion>
        ))}
      </div>
    </div>
  );
}
